import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { bankingAPI, companyAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { useCompany } from '../common/useCompany';
import { fmtMoney, getDisplayCurrency } from '../../utils/currency';
import { Pencil } from 'lucide-react';

/** Format in a specific currency code (falls back to base display) */
function fmtCur(value, code) {
  const n = Number(value) || 0;
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: code }).format(n);
  } catch (e) {
    return `${code} ${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
}

export default function BankAccounts() {
  const navigate = useNavigate(); const company = useCompany();
  const base = company?.base_currency || getDisplayCurrency() || 'USD';
  const [accounts, setAccounts] = useState([]); const [loading, setLoading] = useState(true); const [showForm, setShowForm] = useState(false);
  const [currencies, setCurrencies] = useState([]); const [rates, setRates] = useState([]);
  const [form, setForm] = useState({}); const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    bankingAPI.accounts().then(setAccounts).catch(e=>toast.error(e.message)).finally(()=>setLoading(false));
    companyAPI.currencies().then(setCurrencies).catch(()=>{});
    companyAPI.exchangeRates().then(setRates).catch(()=>{});
  }, []);

  const rateFor = (code) => {
    if (!code || code === base) return 1;
    const r = rates.find(r => r.from_currency === code && r.to_currency === base);
    return r ? parseFloat(r.rate) : null;
  };

  const refresh = () => bankingAPI.accounts().then(setAccounts).catch(e=>toast.error(e.message));
  const openAdd = () => { setEditingId(null); setForm({}); setShowForm(true); };
  const openEdit = (a) => { setEditingId(a.id); setForm({ bank_name: a.bank_name, account_name: a.account_name, account_number: a.account_number, routing_number: a.routing_number, swift_code: a.swift_code, iban: a.iban, currency: a.currency || '' }); setShowForm(true); };
  const handleSubmit = async(e) => { e.preventDefault();
    try {
      if (editingId) { await bankingAPI.updateAccount(editingId, form); toast.success('Bank account updated'); }
      else { await bankingAPI.createAccount(form); toast.success('Bank account added'); }
      setShowForm(false); refresh();
    } catch(err) { toast.error(err.message); } };

  return (
    <div>
      <div className="toolbar"><h2>Bank Accounts</h2>
        <div className="toolbar-right">
          <button className="btn btn-outline" onClick={()=>navigate('/banking/deposit')}>+ Record Deposit</button>
          <button className="btn btn-outline" onClick={()=>navigate('/banking/transactions?transfer=1')}>Transfer</button>
          <button className="btn btn-outline" onClick={()=>navigate('/banking/write-check')}>Write Check</button>
          <button className="btn btn-outline" onClick={()=>navigate('/banking/transactions')}>Transactions</button>
          <button className="btn btn-outline" onClick={()=>navigate('/banking/reconciliation')}>Reconciliation</button>
          <button className="btn btn-primary" onClick={openAdd}>+ Add Bank Account</button>
        </div>
      </div>
      {loading ? <div className="spinner" style={{margin:'40px auto'}} /> : (
        <div className="stats-grid">
          {accounts.map(a => {
            const cur = a.currency || base;
            const fx = rateFor(cur);
            const baseEquiv = fx ? (Number(a.bank_balance) || 0) * fx : null;
            return (
              <div key={a.id} className="stat-card primary" style={{cursor:'pointer'}} onClick={()=>navigate('/banking/transactions?bankAccountId='+a.id)}>
                <div style={{display:'flex', justifyContent:'space-between', alignItems:'flex-start'}}>
                  <div className="stat-label">{a.bank_name || 'Bank Account'} <span className="badge badge-posted" style={{fontSize:10, marginLeft:6}}>{cur}</span></div>
                  <button type="button" className="btn btn-outline" onClick={(e)=>{e.stopPropagation(); openEdit(a);}} style={{padding:'3px 8px', fontSize:11, height:24, display:'inline-flex', alignItems:'center', gap:4}}>
                    <Pencil size={11} /> Edit
                  </button>
                </div>
                <div className="stat-value">{fmtCur(a.bank_balance, cur)}</div>
                {baseEquiv != null && cur !== base && (
                  <div className="stat-change" style={{color:'#166534', fontWeight:600}}>
                    ≈ {fmtCur(baseEquiv, base)} ({base}) &nbsp;•&nbsp; 1 {cur} = {fx.toLocaleString('en-US',{minimumFractionDigits:2, maximumFractionDigits:2})} {base}
                  </div>
                )}
                {cur !== base && baseEquiv == null && (
                  <div className="stat-change text-muted">Set an exchange rate in Settings → Currencies</div>
                )}
                <div className="stat-change text-muted">{a.account_name} • {a.account_number || 'No account #'}</div>
                {a.last_reconciled_date && <div className="stat-change text-muted">Last reconciled: {a.last_reconciled_date}</div>}
              </div>
            );
          })}
          {accounts.length === 0 && <div className="empty-state" style={{gridColumn:'1/-1'}}><div className="empty-icon">🏦</div><h3>No bank accounts</h3><p>Add a bank account to start tracking your money.</p><button className="btn btn-primary" onClick={openAdd}>+ Add Bank Account</button></div>}
        </div>
      )}
      {showForm && (<div className="modal-overlay" onClick={()=>setShowForm(false)}><div className="modal" onClick={e=>e.stopPropagation()}>
        <div className="modal-header"><h3>{editingId ? 'Edit Bank Account' : 'Add Bank Account'}</h3><button className="btn-ghost" onClick={()=>setShowForm(false)}>✕</button></div>
        <form onSubmit={handleSubmit}><div className="modal-body">
          <div className="form-grid">
            <div className="form-group"><label>Bank Name</label><input className="form-control" value={form.bank_name||''} onChange={e=>setForm({...form,bank_name:e.target.value})}/></div>
            <div className="form-group"><label>Account Name</label><input className="form-control" value={form.account_name||''} onChange={e=>setForm({...form,account_name:e.target.value})}/></div>
            <div className="form-group"><label>Currency</label>
              <select className="form-control" value={form.currency||''} onChange={e=>setForm({...form,currency:e.target.value})}>
                <option value="">{base} (Base)</option>
                {currencies.filter(c=>c.code!==base).map(c=><option key={c.code} value={c.code}>{c.code} — {c.name}</option>)}
              </select>
            </div>
            <div className="form-group"><label>Account Number</label><input className="form-control" value={form.account_number||''} onChange={e=>setForm({...form,account_number:e.target.value})}/></div>
            <div className="form-group"><label>Routing Number</label><input className="form-control" value={form.routing_number||''} onChange={e=>setForm({...form,routing_number:e.target.value})}/></div>
            <div className="form-group"><label>SWIFT Code</label><input className="form-control" value={form.swift_code||''} onChange={e=>setForm({...form,swift_code:e.target.value})}/></div>
            <div className="form-group"><label>IBAN</label><input className="form-control" value={form.iban||''} onChange={e=>setForm({...form,iban:e.target.value})}/></div>
          </div>
        </div>
        <div className="modal-footer"><button type="button" className="btn btn-outline" onClick={()=>setShowForm(false)}>Cancel</button><button type="submit" className="btn btn-primary">{editingId ? 'Save Changes' : 'Add Account'}</button></div>
        </form>
      </div></div>)}
    </div>
  );
}